import {Button, Space, Table, Tooltip} from "antd";
import {FormikProps} from "formik";
import React, {FC, memo, useState} from "react";
import {ExpandableConfig} from "antd/lib/table/interface";
import moment, {Moment} from "moment";
import {EditOutlined, LockOutlined} from "@ant-design/icons";
import {
    Location,
    NewCourseInput,
    NewCourseLessonInput,
    EditCourseInput,
    EditCourseLessonInput,
    CourseInstructorIdInput,
    GetMultipleCourseLessonsCollisionCheckQuery,
    GetHolidaysByCompanyAndDateRangeQuery,
    GetLessonsThatAreNotDeletableQuery,
} from "../../generated/graphql";
import {ISODateString} from "../../helpers/typeScriptHelpers";
import i18n from "../../services/i18n";
import {EditableDate, EditableLocation, EditableInstructor, EditableStartDateTime, EditableEndDateTime, Actions, RowIndex, CompactLocation, CompactInstructor} from "./CourseViews/CourseLessonViews";
import {sortByStartDateTime} from "../../helpers/sortingFunctions";
import css from "./CourseCreateDateList.less";
import {getlessonId} from "./helper/getLessonId";
import HolidayTag from "../../views/HolidayTag/HolidayTag";
import useIsCompactView from "../../hooks/useIsCompactView";
import CourseLessonEditDrawer from "./CourseLessonEditDrawer";

type PossibleFormikPropsType = NewCourseInput | EditCourseInput;
export type PossibleFormikPropsTypeWithDataIndex = PossibleFormikPropsType & {
    dataIndex: number;
};

type CourseCreateDateListProps = {
    formikProps: FormikProps<PossibleFormikPropsType>;
    handleRemove: (index: number) => void;
    collitions: GetMultipleCourseLessonsCollisionCheckQuery["multipleCourseLessonsCollisionCheck"];
    holidays?: GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"];
    lessonsThatAreNotDeletable?: GetLessonsThatAreNotDeletableQuery["getLessonsThatAreNotDeletable"];
    billedLessonIds?: string[];
};

const rowStyle: React.CSSProperties = {
    padding: 0,
};

const renderIndex = (value: unknown, record: unknown, rowIndex: number, holiday?: GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"][number]) => {
    const index = rowIndex;

    return <RowIndex rowIndex={index} style={{...rowStyle}} holiday={holiday} />;
};

/** Prueft ob eine Kurseinheit abgerechnet ist */
const checkIsRowBilled = (record: PossibleFormikPropsTypeWithDataIndex, billedLessonIds?: string[]): boolean => {
    if (!billedLessonIds || billedLessonIds.length === 0) return false;
    const lessonId = getlessonId(record) ?? "";
    return billedLessonIds.indexOf(lessonId) !== -1;
};

const renderDate =
    (formikProps: FormikProps<PossibleFormikPropsType>, billedLessonIds?: string[]) =>
    (date: ISODateString, record: PossibleFormikPropsTypeWithDataIndex) =>
        (
            <EditableDate
                date={date}
                index={record.dataIndex}
                formikProps={formikProps}
                testId="EditableDate"
                placeholder={i18n.containers.courses.CourseCreate.firstCourseLessonDate.placeholder()}
                // style={{width: "95px"}}
                style={{...rowStyle}}
                disabled={checkIsRowBilled(record, billedLessonIds)}
            />
        );

const renderLocation =
    (formikProps: FormikProps<PossibleFormikPropsType>, billedLessonIds?: string[]) =>
    (locationId: Location["id"], record: PossibleFormikPropsTypeWithDataIndex) =>
        <EditableLocation formikProps={formikProps} index={record.dataIndex} testId="EditableLocationComponent" style={{...rowStyle}} disabled={checkIsRowBilled(record, billedLessonIds)} />;

const renderInstructors =
    (formikProps: FormikProps<PossibleFormikPropsType>, billedLessonIds?: string[]) =>
    (instructors: Array<CourseInstructorIdInput>, record: PossibleFormikPropsTypeWithDataIndex) =>
        <EditableInstructor maxTagCount={1} selectedInstructors={instructors} index={record.dataIndex} formikProps={formikProps} testId="EditableInstructor" style={{...rowStyle}} disabled={checkIsRowBilled(record, billedLessonIds)} />;

const renderStartTime =
    (formikProps: FormikProps<PossibleFormikPropsType>, billedLessonIds?: string[]) =>
    (startTime: ISODateString, record: PossibleFormikPropsTypeWithDataIndex) =>
        <EditableStartDateTime startTime={startTime} index={record.dataIndex} formikProps={formikProps} testId="EditableStartDateTime" style={{width: "70px", ...rowStyle}} disabled={checkIsRowBilled(record, billedLessonIds)} />;

const renderEndTime =
    (formikProps: FormikProps<PossibleFormikPropsType>, billedLessonIds?: string[]) =>
    (endTime: ISODateString, record: PossibleFormikPropsTypeWithDataIndex) =>
        <EditableEndDateTime endTime={endTime} index={record.dataIndex} formikProps={formikProps} testId="EditableEndDateTime" style={{width: "70px", ...rowStyle}} disabled={checkIsRowBilled(record, billedLessonIds)} />;

const renderActions = (
    handleRemove: (index: number) => void,
    formikProps: FormikProps<PossibleFormikPropsType>,
    lessonsThatAreNotDeletable?: GetLessonsThatAreNotDeletableQuery["getLessonsThatAreNotDeletable"],
    billedLessonIds?: string[],
) => {
    // eslint-disable-next-line react/display-name
    return (_: unknown, lesson: PossibleFormikPropsTypeWithDataIndex, index: number) => (
        <Actions
            handleRemove={handleRemove}
            lesson={lesson}
            index={lesson.dataIndex}
            testId="DeleteCourseLessonButton"
            formikProps={formikProps}
            courseLessonId={getlessonId(lesson) ?? ""}
            lessonsThatAreNotDeletable={lessonsThatAreNotDeletable}
            billedLessonIds={billedLessonIds}
        />
    );
};

const CourseCreateDateList: FC<CourseCreateDateListProps> = ({formikProps, handleRemove, collitions, holidays, lessonsThatAreNotDeletable, billedLessonIds, ...props}) => {
    const isCompact = useIsCompactView();
    const [editDrawerIndex, setEditDrawerIndex] = useState<number | null>(null);

    const dataSource: Array<PossibleFormikPropsTypeWithDataIndex> = (formikProps.values.lessons as Array<NewCourseLessonInput | EditCourseLessonInput>).map((lesson, dataIndex) => ({
        ...lesson,
        dataIndex,
    }));

    const collitionIds = collitions.map((collition) => collition.originalCourseLessonId);

    const isRowBilled = (record: PossibleFormikPropsTypeWithDataIndex): boolean => {
        return checkIsRowBilled(record, billedLessonIds);
    };

    const rowClassName = (record: PossibleFormikPropsTypeWithDataIndex, index: number) => {
        const lessonId = getlessonId(record) ?? "";

        const classNames: string[] = [];

        if (collitionIds.includes(lessonId)) {
            classNames.push(css.rowWithCollisions);
        }

        if (lessonsThatAreNotDeletable?.includes(lessonId)) {
            classNames.push(css.rowWithNotDeletable);
        }

        // Abgerechnete Zeilen markieren
        if (billedLessonIds && billedLessonIds.length > 0 && billedLessonIds.indexOf(lessonId) !== -1) {
            classNames.push(css.rowBilled);
        }

        return classNames.join(" ");
    };

    const expandable: ExpandableConfig<PossibleFormikPropsTypeWithDataIndex> = {
        defaultExpandAllRows: true,
        showExpandColumn: false,
        rowExpandable: (record) => collitionIds.includes(getlessonId(record) ?? ""),
        expandedRowKeys: collitionIds,
        expandedRowClassName: () => css.rowWithCollisionExpanded,
        // eslint-disable-next-line react/display-name
        expandedRowRender: (record) => {
            const collition = collitions.find((c) => c.originalCourseLessonId === getlessonId(record));
            const {course, indexOfLessonInCourse, startDateTime, endDateTime} = collition;
            const start = moment(startDateTime).format("HH:mm");
            const end = moment(endDateTime).format("HH:mm");

            const text = `Achtung! Es gibt eine Überschneidung mit der ${indexOfLessonInCourse + 1} Kurseinheit im Kurs ${course.prefixedCourseNumber} (${start} - ${end}) im selben Becken/Raum.`;

            return <div>{text}</div>;
        },
    };

    const getHolidayInfo = (date: Moment): GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"][number] | undefined => {
        const allHolidaysOfDate = holidays?.filter((holiday) => {
            const holidayStart = moment(holiday.start).startOf("day");
            const holidayEnd = moment(holiday.end).endOf("day");

            return moment(date).isSameOrAfter(holidayStart) && moment(date).isSameOrBefore(holidayEnd);
        });

        const customHoliday = allHolidaysOfDate?.find((holiday) => {
            return holiday.stateCode === "CUSTOM";
        });

        const holidayToShow = customHoliday ?? allHolidaysOfDate?.[0];

        return holidayToShow;
    };

    /** Kompakte Text-Darstellung fuer Datum (nur Anzeige, nicht editierbar) */
    const renderDateCompact = (date: ISODateString, _record: PossibleFormikPropsTypeWithDataIndex) => (
        <span style={{whiteSpace: "nowrap"}}>{moment(date).format("dd DD.MM.YY")}</span>
    );

    /** Kompakte Darstellung fuer Zeitraum (Von - Bis in einer Spalte) */
    const renderTimeCompact = (_: unknown, record: PossibleFormikPropsTypeWithDataIndex) => (
        <span style={{whiteSpace: "nowrap"}}>
            {moment(record.startDateTime).format("HH:mm")} {"\u2013"} {moment(record.endDateTime).format("HH:mm")}
        </span>
    );

    /** Kompakte Darstellung fuer Location (nur Venue-Name) */
    const renderLocationCompact = (_: unknown, record: PossibleFormikPropsTypeWithDataIndex) => (
        <CompactLocation formikProps={formikProps} index={record.dataIndex} />
    );

    /** Kompakte Darstellung fuer Instructor (nur Vornamen) */
    const renderInstructorCompact = (instructors: Array<CourseInstructorIdInput>) => (
        <CompactInstructor instructors={instructors} />
    );

    /** Ermittelt den editDrawerIndex-Record fuer die disabled-Pruefung */
    const isDrawerRecordBilled = (): boolean => {
        if (editDrawerIndex === null) return false;
        const record = dataSource[editDrawerIndex];
        if (!record) return false;
        return checkIsRowBilled(record, billedLessonIds);
    };

    return (
        <>
            <Table<PossibleFormikPropsTypeWithDataIndex>
                dataSource={dataSource}
                rowKey={(record) => getlessonId(record) ?? record.dataIndex}
                pagination={false}
                scroll={{x: isCompact ? undefined : "max-content"}}
                size="small"
                rowClassName={rowClassName}
                expandable={expandable}
                {...props}
            >
                {/* # Spalte */}
                <Table.Column
                    title="#"
                    key="index"
                    render={(value, record: PossibleFormikPropsTypeWithDataIndex, rowIndex) => {
                        const start = moment(record.startDateTime);

                        return renderIndex(value, record, rowIndex, getHolidayInfo(start));
                    }}
                    fixed={isCompact ? undefined : "left"}
                    width="3em"
                    align="center"
                />

                {/* Lock-Icon Spalte - nur im Desktop-Modus */}
                {!isCompact && (
                    <Table.Column
                        title=""
                        key="billedStatus"
                        width={40}
                        render={(_: unknown, record: PossibleFormikPropsTypeWithDataIndex) => {
                            if (!isRowBilled(record)) {
                                return null;
                            }
                            return (
                                <Tooltip title={i18n.containers.courses.CourseEdit.billedLessons.lessonLocked.tooltip()}>
                                    <LockOutlined style={{color: "#faad14", fontSize: "16px"}} />
                                </Tooltip>
                            );
                        }}
                    />
                )}

                {/* Datum */}
                <Table.Column
                    title={i18n.containers.courses.CourseCreate.courseLessons.date.label()}
                    key="date"
                    dataIndex="startDateTime"
                    render={isCompact ? renderDateCompact : renderDate(formikProps, billedLessonIds)}
                    sorter={sortByStartDateTime}
                    sortDirections={[]}
                    sortOrder="ascend"
                />

                {/* Desktop: Von und Bis als separate Spalten */}
                {!isCompact && (
                    <Table.Column
                        title={i18n.containers.courses.CourseCreate.courseLessons.startTime.label()}
                        key="startDateTime"
                        dataIndex="startDateTime"
                        render={renderStartTime(formikProps, billedLessonIds)}
                    />
                )}
                {!isCompact && (
                    <Table.Column
                        title={i18n.containers.courses.CourseCreate.courseLessons.endTime.label()}
                        key="endDateTime"
                        dataIndex="endDateTime"
                        render={renderEndTime(formikProps, billedLessonIds)}
                    />
                )}

                {/* Compact: Zeit als eine Spalte (Von - Bis) */}
                {isCompact && (
                    <Table.Column
                        title="Zeit"
                        key="time"
                        render={renderTimeCompact}
                    />
                )}

                {/* Bad/Raum */}
                <Table.Column
                    title={i18n.containers.courses.CourseCreate.courseLessons.venue.label()}
                    key="locationId"
                    dataIndex="locationId"
                    render={isCompact ? renderLocationCompact : renderLocation(formikProps, billedLessonIds)}
                />

                {/* Kursleiter */}
                <Table.Column
                    title={i18n.containers.courses.CourseCreate.courseLessons.instructor.label()}
                    key="instructors"
                    dataIndex="instructors"
                    render={isCompact ? renderInstructorCompact : renderInstructors(formikProps, billedLessonIds)}
                />

                {/* Actions */}
                <Table.Column
                    title={i18n.containers.courses.CourseCreate.courseLessons.action()}
                    key="actions"
                    render={isCompact
                        ? (_: unknown, record: PossibleFormikPropsTypeWithDataIndex, index: number) => {
                            const isBilled = checkIsRowBilled(record, billedLessonIds);
                            return (
                                <Space size={4}>
                                    <Button
                                        type="link"
                                        icon={<EditOutlined />}
                                        size="small"
                                        onClick={() => setEditDrawerIndex(record.dataIndex)}
                                        disabled={isBilled}
                                        data-testid="EditCourseLessonDrawerButton"
                                    />
                                    {renderActions(handleRemove, formikProps, lessonsThatAreNotDeletable, billedLessonIds)(_, record, index)}
                                </Space>
                            );
                        }
                        : renderActions(handleRemove, formikProps, lessonsThatAreNotDeletable, billedLessonIds)
                    }
                    align="center"
                    fixed={isCompact ? undefined : "right"}
                />
            </Table>

            {/* Edit Drawer fuer kompakte Ansicht */}
            {isCompact && (
                <CourseLessonEditDrawer
                    open={editDrawerIndex !== null}
                    onClose={() => setEditDrawerIndex(null)}
                    index={editDrawerIndex ?? 0}
                    formikProps={formikProps}
                    disabled={isDrawerRecordBilled()}
                />
            )}
        </>
    );
};

export default memo(CourseCreateDateList);
